import React, { useState } from "react";
import {
  Calendar,
  BarChart3,
  Clock,
  Eye,
  Target,
  Monitor,
  Smartphone,
  Tv,
  Tablet,
  PlayCircle,
  Image as ImageIcon,
} from "lucide-react";
import { ClientLogo } from "../../components/ClientLogo";
import type { CampaignReportResponse } from "../../types";
import { MetricCard } from "../../components/MetricCard";
import { DonutChart } from "../../components/DonutChart";
import { BarChart } from "../../components/BarChart";
import { SectionToggleWrapper } from "../../components/SectionToggleWrapper";
import { AiInsightsSection } from "../../components/AiInsightsSection";
import {
  adTypeColors,
  buildOffsiteLayoutViewModel,
  formatCurrencyValue,
  formatImpressions,
  formatRoasValue,
  parseNumber,
  type RankedMetric,
} from "./offsiteLayoutViewModel";

interface OffsiteLayoutProps {
  data: CampaignReportResponse;
  reportId?: string;
  onNewUpload?: () => void;
  clientId?: string;
  reportRetailer?: string;
}

/**
 * Specialized layout for Offsite Display Ads reports.
 *
 * Displays dimensional breakdowns:
 * - Ad Group performance (targeting strategies)
 * - Device Type distribution
 * - Media Type comparison (Video vs Display)
 */
export const OffsiteLayout: React.FC<OffsiteLayoutProps> = ({
  data,
  reportId,
  clientId,
  reportRetailer,
}) => {
  const resolvedClientId = clientId ?? "ajinomoto";

  const [hiddenSections, setHiddenSections] = useState<Record<string, boolean>>(
    {},
  );

  const hideSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: true }));

  const restoreSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: false }));

  const isSectionHidden = (sectionId: string) =>
    Boolean(hiddenSections[sectionId]);

  // Toggle states for charts
  const [creativeMetric, setCreativeMetric] =
    useState<RankedMetric>("impressions");
  const [audienceMetric, setAudienceMetric] =
    useState<RankedMetric>("impressions");
  const [creativeLimit, setCreativeLimit] = useState<"top5" | "all">("top5");
  const [audienceLimit, setAudienceLimit] = useState<"top5" | "all">("top5");
  // Conversion filter states for WMC reports
  const [minConversions, setMinConversions] = useState<number>(1);
  const {
    adGroupList,
    adTypePerformance,
    audienceMetricLabel,
    audiences,
    averageRoasDisplay,
    campaignCountsByMedia,
    creativeData,
    creativeMetricLabel,
    deviceAwareness,
    deviceChartColors,
    deviceChartData,
    hasAdTypePerformance,
    hasCreativeSummary,
    isWmcReport,
    mediaAwareness,
    retailerSummaries,
    retailerTitleText,
    shouldShowRetailerCards,
    summary,
    totalConversions,
    totalImpressions,
    totalSalesAmount,
    totalSpendAmount,
  } = buildOffsiteLayoutViewModel({
    audienceLimit,
    audienceMetric,
    creativeLimit,
    creativeMetric,
    data,
    minConversions,
    reportRetailer,
  });

  const deviceStyleMap: Record<
    string,
    { container: string; iconWrapper: string }
  > = {
    "ConnectedTV": {
      container:
        "border-amber-200 dark:border-amber-700 bg-white dark:bg-gray-900/40",
      iconWrapper:
        "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-200",
    },
    "Mobile": {
      container:
        "border-emerald-200 dark:border-emerald-700 bg-white dark:bg-gray-900/40",
      iconWrapper:
        "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-200",
    },
    "PC": {
      container:
        "border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-900/40",
      iconWrapper:
        "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200",
    },
    "Tablet": {
      container:
        "border-purple-200 dark:border-purple-700 bg-white dark:bg-gray-900/40",
      iconWrapper:
        "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200",
    },
    "Display": {
      container:
        "border-cyan-200 dark:border-cyan-700 bg-white dark:bg-gray-900/40",
      iconWrapper:
        "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-200",
    },
    "Online Video": {
      container:
        "border-rose-200 dark:border-rose-700 bg-white dark:bg-gray-900/40",
      iconWrapper:
        "bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-200",
    },
  };
  const defaultDeviceStyle = {
    container:
      "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40",
    iconWrapper:
      "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-100",
  };

  return (
    <div className="py-16 px-8" data-dashboard>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Offsite Display Campaign Report: {retailerTitleText}
              </h1>
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  Offsite Display Ads
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {summary.date_range_start} to {summary.date_range_end}
                </span>
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-3 h-3" />
                  {data.records_processed} records
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {summary.total_days} days
                </span>
              </div>
            </div>
            <div>
              <ClientLogo
                clientId={resolvedClientId}
                className="h-16 object-contain"
                fallback={
                  <div className="h-16 w-16 bg-gray-200 dark:bg-[#5B5353] rounded" />
                }
              />
            </div>
          </div>
        </div>

        <AiInsightsSection data={data} reportId={reportId} />

        {/* Key Awareness Metrics */}
        <SectionToggleWrapper
          sectionId="offsite-overview"
          title="Awareness & Reach Performance"
          hidden={isSectionHidden("offsite-overview")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Awareness & Reach Performance
          </h2>
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${isWmcReport ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
            <MetricCard
              title="Total Impressions"
              value={
                totalImpressions >= 1000000
                  ? `${(totalImpressions / 1000000).toFixed(1)}M`
                  : totalImpressions >= 1000
                    ? `${(totalImpressions / 1000).toFixed(0)}k`
                    : totalImpressions.toLocaleString()
              }
              trend="neutral"
            />
            <MetricCard
              title="CTR"
              value={`${(parseFloat(summary.average_ctr) * 100).toFixed(2)}%`}
              subtitle={`${summary.total_clicks.toLocaleString()} total clicks`}
            />
            {isWmcReport && (
              <MetricCard
                title="Total Conversions"
                value={Math.round(totalConversions).toLocaleString()}
                subtitle="Attributed conversions"
              />
            )}
            <MetricCard
              title="Total Sales"
              value={formatCurrencyValue(totalSalesAmount)}
            />
            <MetricCard
              title="Average ROAS"
              value={averageRoasDisplay}
              subtitle={`Spend ${formatCurrencyValue(summary.total_spend)}`}
              trend="neutral"
            />
          </div>
          {shouldShowRetailerCards && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {retailerSummaries.map((retailer) => {
                const impressions = parseNumber(retailer.total_impressions);
                const share =
                  typeof retailer.percentage_of_impressions === "number"
                    ? retailer.percentage_of_impressions
                    : totalImpressions > 0
                      ? (impressions / totalImpressions) * 100
                      : 0;
                return (
                  <MetricCard
                    key={retailer.retailer}
                    title={`${retailer.retailer} Impressions`}
                    value={formatImpressions(impressions)}
                    subtitle={`${share.toFixed(1)}% of impressions`}
                    trend="neutral"
                  />
                );
              })}
            </div>
          )}
        </SectionToggleWrapper>

        {/* Device & Media Performance */}
        <SectionToggleWrapper
          sectionId="offsite-device-media"
          title="Device & Media Performance"
          hidden={isSectionHidden("offsite-device-media")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            Device & Media Performance
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Device Type Distribution */}
            {deviceAwareness.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col h-full">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Impressions by Device Type
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Total: {summary.total_impressions.toLocaleString()}{" "}
                  impressions
                </p>
                <DonutChart data={deviceChartData} colors={deviceChartColors} />

                {/* Device Type Cards */}
                <div className="mt-8 flex flex-col gap-2.5">
                  {deviceAwareness.map((device) => (
                    <div
                      key={device.deviceType}
                      className={`px-3 py-3 rounded-lg border flex flex-col gap-1.5 ${
                        deviceStyleMap[device.deviceType]?.container ??
                        defaultDeviceStyle.container
                      }`}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-center gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span
                            className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${
                              deviceStyleMap[device.deviceType]?.iconWrapper ??
                              defaultDeviceStyle.iconWrapper
                            }`}
                          >
                            {device.deviceType === "ConnectedTV" && (
                              <Tv className="w-5 h-5" />
                            )}
                            {device.deviceType === "Mobile" && (
                              <Smartphone className="w-5 h-5" />
                            )}
                            {device.deviceType === "PC" && (
                              <Monitor className="w-5 h-5" />
                            )}
                            {device.deviceType === "Tablet" && (
                              <Tablet className="w-5 h-5" />
                            )}
                            {device.deviceType === "Display" && (
                              <ImageIcon className="w-5 h-5" />
                            )}
                            {device.deviceType === "Online Video" && (
                              <PlayCircle className="w-5 h-5" />
                            )}
                          </span>
                          <span className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                            {device.deviceType}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
                          <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Impressions
                            </div>
                            <div className="text-base font-semibold text-gray-900 dark:text-white">
                              {device.impressions.toLocaleString()}
                            </div>
                          </div>
                          <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                              Share
                            </div>
                            <div className="text-base font-semibold text-gray-900 dark:text-white">
                              {device.impressionShare.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Media Type Comparison */}
            {mediaAwareness.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Video vs Display Performance
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Total impressions: {totalImpressions.toLocaleString()}
                </p>
                {mediaAwareness.length > 0 && (
                  <div className="mb-6 h-56">
                    <BarChart
                      labels={mediaAwareness.map((mt) => mt.mediaType)}
                      datasets={[
                        {
                          label: "Impressions",
                          data: mediaAwareness.map((mt) => mt.impressions),
                          backgroundColor: mediaAwareness.map((mt) => mt.color),
                        },
                      ]}
                      horizontal={true}
                      showLegend={false}
                      showDataLabels={true}
                      height="h-full"
                      valueFormatter={formatImpressions}
                    />
                  </div>
                )}

                {/* Media Type Cards */}
                <div className="mt-2 flex flex-col gap-3">
                  {mediaAwareness.map((mt) => (
                    <div
                      key={mt.mediaType}
                      className={`p-5 rounded-xl border flex flex-col gap-4 ${
                        mt.mediaType === "Video"
                          ? "border-red-200 dark:border-red-700"
                          : "border-cyan-200 dark:border-cyan-700"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {mt.mediaType === "Video" && (
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-200">
                              <PlayCircle className="w-5 h-5" />
                            </span>
                          )}
                          {mt.mediaType === "Display" && (
                            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cyan-100 text-cyan-600 dark:bg-cyan-900/40 dark:text-cyan-200">
                              <ImageIcon className="w-5 h-5" />
                            </span>
                          )}
                          <span className="text-lg font-bold text-gray-900 dark:text-white ml-2">
                            {mt.mediaType}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-[11px] uppercase tracking-wide text-gray-600 dark:text-gray-400">
                            Share of Impressions
                          </div>
                          <div className="text-base font-semibold text-gray-900 dark:text-white">
                            {mt.impressionShare.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Impressions
                          </div>
                          <div className="text-lg font-semibold text-gray-900 dark:text-white">
                            {mt.impressions.toLocaleString()}
                          </div>
                        </div>
                        <div className="rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700">
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Active Campaigns
                          </div>
                          <div className="text-lg font-semibold text-gray-900 dark:text-white">
                            {(
                              campaignCountsByMedia[mt.mediaType] ?? 0
                            ).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionToggleWrapper>

        {/* Budget Distribution by Ad Type */}
        {hasAdTypePerformance && (
          <SectionToggleWrapper
            sectionId="offsite-budget-distribution"
            title="Budget Distribution by Ad Type"
            hidden={isSectionHidden("offsite-budget-distribution")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Spend Allocation
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Total spend: {formatCurrencyValue(totalSpendAmount)}
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Donut Chart Card */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col justify-center">
                  <DonutChart
                    data={adTypePerformance.map((item) => ({
                      brand_name: item.type,
                      units: item.spend,
                      percentage: totalSpendAmount > 0 ? (item.spend / totalSpendAmount) * 100 : 0,
                    }))}
                    colors={adTypePerformance.map((item) => adTypeColors[item.type])}
                  />
                </div>
                {/* Spend Breakdown Cards */}
                <div className="flex flex-col gap-2.5 justify-center">
                  {adTypePerformance.map((item) => {
                    const spendPercentage = totalSpendAmount > 0 ? (item.spend / totalSpendAmount) * 100 : 0;
                    return (
                      <div
                        key={item.type}
                        className="px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full"
                              style={{ backgroundColor: adTypeColors[item.type], opacity: 0.2 }}
                            >
                              <span
                                className="text-xl font-bold"
                                style={{ color: adTypeColors[item.type] }}
                              >
                                •
                              </span>
                            </span>
                            <span className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                              {item.type}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                              <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                Spend
                              </div>
                              <div className="text-base font-semibold text-gray-900 dark:text-white">
                                {formatCurrencyValue(item.spend)}
                              </div>
                            </div>
                            <div className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                              <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                % of Budget
                              </div>
                              <div className="text-base font-semibold text-gray-900 dark:text-white">
                                {spendPercentage.toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </SectionToggleWrapper>
        )}

        {/* Ad Type Performance */}
        {hasAdTypePerformance && (
          <SectionToggleWrapper
            sectionId="offsite-ad-type-performance"
            title="Ad Type Performance"
            hidden={isSectionHidden("offsite-ad-type-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {isWmcReport ? "Conversions by Ad Type" : "Sales by Ad Type"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {adTypePerformance.map((item) => (
                  <div
                    key={item.type}
                    className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50 dark:bg-gray-900/40 flex flex-col gap-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800 dark:text-white">
                        {item.type}
                      </span>
                      <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {isWmcReport ? "Conversions" : "Sales"}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {isWmcReport
                        ? Math.round(item.conversions).toLocaleString()
                        : formatCurrencyValue(item.sales)}
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 dark:text-gray-400">
                      <div>
                        <div className="text-xs uppercase tracking-wide">
                          Sales
                        </div>
                        <div className="text-base font-semibold text-gray-900 dark:text-white">
                          {formatCurrencyValue(item.sales)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide">
                          ROAS
                        </div>
                        <div className="text-base font-semibold text-gray-900 dark:text-white">
                          {formatRoasValue(item.roas)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  ROAS by Ad Type
                </h3>
                <BarChart
                  labels={adTypePerformance.map((item) => item.type)}
                  datasets={[
                    {
                      label: "ROAS",
                      data: adTypePerformance.map((item) => item.roas),
                      backgroundColor: adTypePerformance.map(
                        (item) => adTypeColors[item.type],
                      ),
                    },
                  ]}
                  horizontal={false}
                  showLegend={false}
                  showDataLabels={true}
                  height="h-80"
                  valueFormatter={formatRoasValue}
                />
              </div>
            </div>
          </SectionToggleWrapper>
        )}

        {/* Creative Performance Section */}
        {hasCreativeSummary && (
          <SectionToggleWrapper
            sectionId="offsite-creative-performance"
            title="Creative Performance"
            hidden={isSectionHidden("offsite-creative-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Top Creatives by {creativeMetricLabel}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {creativeLimit === "top5" ? "top 5" : "all"} creative
                    assets
                  </p>
                </div>
                <div
                  className="flex gap-3 data-exclude-from-export"
                  data-exclude-from-export
                >
                  {/* Top 5 / All toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCreativeLimit("top5")}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        creativeLimit === "top5"
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      Top 5
                    </button>
                    <button
                      onClick={() => setCreativeLimit("all")}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        creativeLimit === "all"
                          ? "bg-purple-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      All
                    </button>
                  </div>
                  {/* Impressions / CTR / Sales toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCreativeMetric("impressions")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        creativeMetric === "impressions"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      Impressions
                    </button>
                    <button
                      onClick={() => setCreativeMetric("ctr")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        creativeMetric === "ctr"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      CTR
                    </button>
                    <button
                      onClick={() => setCreativeMetric("sales")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        creativeMetric === "sales"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      Sales
                    </button>
                    {isWmcReport && (
                      <button
                        onClick={() => setCreativeMetric("conversions")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          creativeMetric === "conversions"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                        }`}
                      >
                        Conversions
                      </button>
                    )}
                  </div>
                  {/* Min conversions filter - only show when conversions metric is selected */}
                  {isWmcReport && creativeMetric === "conversions" && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Min:
                      </span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={minConversions}
                        onChange={(event) => {
                          const next = Number.parseInt(event.target.value, 10);
                          setMinConversions(Number.isFinite(next) ? next : 0);
                        }}
                        className="w-20 px-3 py-2 rounded-lg text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500"
                        aria-label="Minimum conversions"
                      />
                    </div>
                  )}
                </div>
              </div>
              {creativeData.length > 0 ? (
                <BarChart
                  labels={creativeData.map((item) => item.creative)}
                  datasets={[
                    {
                      label: creativeMetricLabel,
                      data: creativeData.map((item) =>
                        creativeMetric === "impressions"
                          ? item.impressions
                          : creativeMetric === "sales"
                            ? item.sales
                            : creativeMetric === "conversions"
                              ? item.conversions
                              : item.ctr,
                      ),
                      backgroundColor: "#8b5cf6",
                    },
                  ]}
                  horizontal={true}
                  showLegend={false}
                  showDataLabels={true}
                  valueFormatter={
                    creativeMetric === "impressions"
                      ? formatImpressions
                      : creativeMetric === "sales"
                        ? formatCurrencyValue
                        : creativeMetric === "conversions"
                          ? (value) => Math.round(value).toLocaleString()
                          : (value) => `${value.toFixed(2)}%`
                  }
                  height="h-96"
                />
              ) : (
                <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-6 text-sm text-gray-600 dark:text-gray-300">
                  No creatives match the current filter.
                </div>
              )}
            </div>
          </SectionToggleWrapper>
        )}

        {/* Audience Performance Section */}
        {audiences.length > 0 && (
          <SectionToggleWrapper
            sectionId="offsite-audience-performance"
            title="Audience Performance"
            hidden={isSectionHidden("offsite-audience-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Top Audiences by {audienceMetricLabel}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {audienceLimit === "top5" ? "top 5" : "all"}{" "}
                    audiences (rolled up across all placements)
                  </p>
                </div>
                <div
                  className="flex gap-3 data-exclude-from-export"
                  data-exclude-from-export
                >
                  {/* Top 5 / All toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAudienceLimit("top5")}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        audienceLimit === "top5"
                          ? "bg-green-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      Top 5
                    </button>
                    <button
                      onClick={() => setAudienceLimit("all")}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        audienceLimit === "all"
                          ? "bg-green-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      All
                    </button>
                  </div>
                  {/* Metric toggle */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setAudienceMetric("impressions")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        audienceMetric === "impressions"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      Impressions
                    </button>
                    <button
                      onClick={() => setAudienceMetric("ctr")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        audienceMetric === "ctr"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      CTR
                    </button>
                    <button
                      onClick={() => setAudienceMetric("sales")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        audienceMetric === "sales"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                      }`}
                    >
                      Sales
                    </button>
                    {isWmcReport && (
                      <button
                        onClick={() => setAudienceMetric("conversions")}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          audienceMetric === "conversions"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                        }`}
                      >
                        Conversions
                      </button>
                    )}
                  </div>
                  {/* Min conversions filter - only show when conversions metric is selected */}
                  {isWmcReport && audienceMetric === "conversions" && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Min:
                      </span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={minConversions}
                        onChange={(event) => {
                          const next = Number.parseInt(event.target.value, 10);
                          setMinConversions(Number.isFinite(next) ? next : 0);
                        }}
                        className="w-20 px-3 py-2 rounded-lg text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500"
                        aria-label="Minimum conversions"
                      />
                    </div>
                  )}
                </div>
              </div>
              <BarChart
                labels={audiences.map((a) => a.audience)}
                datasets={[
                  {
                    label: audienceMetricLabel,
                    data: audiences.map((a) =>
                      audienceMetric === "impressions"
                        ? a.impressions
                        : audienceMetric === "sales"
                          ? a.sales
                          : audienceMetric === "conversions"
                            ? a.conversions
                            : a.ctr,
                    ),
                    backgroundColor: "#10b981",
                  },
                ]}
                horizontal={true}
                showLegend={false}
                showDataLabels={true}
                valueFormatter={
                  audienceMetric === "impressions"
                    ? formatImpressions
                    : audienceMetric === "sales"
                      ? formatCurrencyValue
                      : audienceMetric === "conversions"
                        ? (value) => Math.round(value).toLocaleString()
                        : (value) => `${value.toFixed(2)}%`
                }
                height="h-96"
              />
            </div>
          </SectionToggleWrapper>
        )}

        {/* Ad Group Performance Section
        {adGroupList.length > 0 && (
          <SectionToggleWrapper
            sectionId="offsite-ad-group-performance"
            title="Targeting Strategy Performance"
            hidden={isSectionHidden("offsite-ad-group-performance")}
            onHide={hideSection}
            onRestore={restoreSection}
            className="mb-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Targeting Strategy Performance (Top 10)
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Impressions by Ad Group
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Showing top 10 ad groups by impression volume
              </p>
              <BarChart
                labels={adGroupLabels}
                datasets={[
                  {
                    label: "Impressions",
                    data: adGroupImpressions,
                    backgroundColor: "#3b82f6",
                  },
                ]}
                horizontal={true}
                showLegend={false}
                valueFormatter={formatImpressions}
                height="h-96"
              />
            </div>
          </SectionToggleWrapper>
        )}
        */}

        {/* Daily Performance Trend
        <SectionToggleWrapper
          sectionId="offsite-daily-trend"
          title="Daily Awareness Trend"
          hidden={isSectionHidden("offsite-daily-trend")}
          onHide={hideSection}
          onRestore={restoreSection}
          className="mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Daily Awareness Trend
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Impressions (000s) over {summary.total_days} days
            </p>
            <BarChart
              labels={dailyLabels}
              datasets={dailyAwarenessDatasets}
              stacked={false}
              horizontal={false}
              showLegend={true}
              height="h-96"
            />
          </div>
        </SectionToggleWrapper>
        */}

        {/* Ad Group Details Table */}
        {adGroupList.length > 0 && (
          <SectionToggleWrapper
            sectionId="offsite-ad-group-details"
            title="Ad Group Details"
            hidden={isSectionHidden("offsite-ad-group-details")}
            onHide={hideSection}
            onRestore={restoreSection}
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Ad Group Details
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {adGroupList.length} ad groups sorted by impression volume
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ad Group
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Device
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Media
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Impressions
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Spend
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Impression Share
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        eCPM
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {[...adGroupList]
                      .sort((a, b) => b.total_impressions - a.total_impressions)
                      .map((ag) => {
                        const ecpmValue =
                          ag.total_impressions > 0
                            ? (parseFloat(ag.total_spend) /
                                ag.total_impressions) *
                              1000
                            : 0;
                        return (
                          <tr
                            key={ag.ad_group}
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-xs truncate">
                              {ag.ad_group}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                              {ag.device_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                              {ag.media_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                              {ag.total_impressions.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-semibold text-gray-900 dark:text-white">
                              $
                              {parseFloat(ag.total_spend).toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                },
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                              ${ecpmValue.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-400">
                              {(
                                (ag.total_impressions / totalImpressions) *
                                100
                              ).toFixed(1)}
                              %
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionToggleWrapper>
        )}
      </div>
    </div>
  );
};
